/**
 * La caja negra de la pata de audio del gestor.
 *
 * 🔴 **Existe porque llevamos un día entero sin encontrar la causa.**
 *
 * El 15/10/2026 la voz de Edgar dejó de llegarle a los deudores en mitad de la
 * jornada, tres veces, y cada vez tras un rato distinto: 24 s, 22 s y 14
 * minutos. Lo único que lo arregla es reabrir la sesión. Se quemaron nueve
 * deudores reales —descolgaron, hablaron entre 2 y 12 segundos, y no les
 * contestó nadie— y lo descubrimos tres llamadas tarde cada vez.
 *
 * Lo que se descartó, midiendo: no es la pata reutilizada (el 07/10, 31 de 42
 * llamadas con pata reutilizada llevaban voz), no es el anchorsite de Ámsterdam,
 * no es el permiso del micrófono, y no es otra aplicación abierta —siguió
 * fallando con todas cerradas—. Y el micro **funciona** cuando se mide: hablando
 * acumula 80 millones de veces más energía por segundo que callado.
 *
 * O sea que después de un día solo tenemos descartes. El motivo es que cada
 * medición era una captura de pantalla pedida a mano: solo se ve **el instante
 * en que alguien mira**, nunca **el instante en que se rompe**.
 *
 * Esto graba el instante en que se rompe.
 *
 * ## Qué graba, y por qué cada cosa
 *
 * Cada segundo, durante **toda la sesión** —no solo durante las llamadas, porque
 * no sabemos si se rompe entre ellas—:
 *
 *  - **`currentDirection` del transceptor.** Es el dato que falta para la única
 *    hipótesis viva: la pata del gestor es ENTRANTE, y en `@telnyx/webrtc` el
 *    `addTransceiver({direction:'sendrecv'})` explícito solo corre cuando el
 *    navegador hace la oferta (`if (this.isOffer …)`). Contestando cae en
 *    `addStream()`, que hereda la dirección de lo que ofrezca Telnyx. Y el SDK
 *    **no lee `currentDirection` en ningún sitio**: nadie comprueba en qué
 *    dirección acabó la negociación.
 *  - **La línea `a=` de audio del SDP**, local y remota, tras cada renegociación.
 *    Lo mismo, pero desde la fuente. Hay una renegociación por puente.
 *  - **`track.muted`, `readyState`, `id` y `label`.** Si Windows le quita el
 *    dispositivo a Chrome, la pista dispara `mute` **sin terminar**: sigue viva,
 *    sigue enganchada al emisor, y entrega silencio digital. Encaja con todo lo
 *    medido y **no lo habíamos mirado nunca**.
 *  - **`totalAudioEnergy` y `bytesSent`**, para tener la curva entera y poder
 *    señalar el segundo exacto del corte en vez de deducirlo de la grabación.
 *  - **Los estados de la conexión** (`signalingState`, `connectionState`), para
 *    saber QUÉ renegociación fue la que rompió, si fue una.
 *
 * Y los eventos, con su hora: `mute`/`unmute`/`ended` de la pista,
 * `devicechange` de Windows, `negotiationneeded`, los cambios de estado, y
 * nuestros propios hitos (`call_bridged`, `call_ended`).
 *
 * ## Dos decisiones que no son obvias
 *
 * **Se engancha con `addEventListener`, nunca asignando `pc.onalgo`.** El SDK de
 * Telnyx usa esos huecos para su propio funcionamiento; pisarlos rompería la
 * llamada para diagnosticar por qué se rompe la llamada.
 *
 * **No lee el micrófono.** Ni un `getUserMedia`, ni un `AudioContext` sobre la
 * pista. Todo sale de `getStats()` y de propiedades que ya están ahí. Una
 * segunda captura del dispositivo es exactamente uno de los fallos que estamos
 * persiguiendo — ver el comentario de `asegurarMicrofono`.
 */

/** Cada cuánto se toma una muestra. */
export const MUESTREO_MS = 1000;

/**
 * Cuántas muestras se guardan.
 *
 * 2.400 a una por segundo son **40 minutos**. Las tres roturas del 15/10
 * tardaron 24 s, 22 s y 14 minutos, así que 40 cubre de sobra la más lenta y el
 * buffer cabe en memoria sin que haya que pensarlo (unos 300 KB).
 */
export const MAX_MUESTRAS = 2400;

/** Lo mismo para los eventos, que son muchos menos. */
export const MAX_EVENTOS = 400;

/** Saca la línea de dirección (`a=sendrecv`…) de la sección de audio de un SDP. */
export function direccionDelSdp(sdp) {
  if (typeof sdp !== 'string') return null;
  // Solo la sección `m=audio`: un SDP de dos secciones tiene más líneas `a=` y
  // quedarse con la primera del fichero daría la de otra sección.
  const desde = sdp.indexOf('m=audio');
  if (desde === -1) return null;
  const siguiente = sdp.indexOf('\nm=', desde + 1);
  const seccion = sdp.slice(desde, siguiente === -1 ? undefined : siguiente);
  const m = seccion.match(/^a=(sendrecv|sendonly|recvonly|inactive)\s*$/m);
  return m ? m[1] : null;
}

/** El transceptor y el emisor de audio de una conexión, si los hay. */
function piezasDeAudio(pc) {
  if (!pc) return {};
  const transceptores = typeof pc.getTransceivers === 'function' ? pc.getTransceivers() : [];
  const tx = transceptores.find(
    (t) => t?.sender?.track?.kind === 'audio' || t?.receiver?.track?.kind === 'audio'
  );
  const emisores = typeof pc.getSenders === 'function' ? pc.getSenders() : [];
  const sender = emisores.find((s) => s?.track?.kind === 'audio');
  return { tx, sender, track: sender?.track || tx?.sender?.track || null };
}

/** Lo que se puede leer de `getStats()` sin tocar el micrófono. */
function deLasEstadisticas(report) {
  const out = { energia: null, nivel: null, bytes: null, paquetes: null, pista: false };
  if (!report || typeof report.forEach !== 'function') return out;
  report.forEach((s) => {
    if (s.type === 'media-source' && s.kind === 'audio') {
      out.pista = true;
      if (typeof s.totalAudioEnergy === 'number') out.energia = s.totalAudioEnergy;
      if (typeof s.audioLevel === 'number') out.nivel = s.audioLevel;
      // 🔴 La salud del cancelador de eco, que es lo que de verdad falló.
      //
      // `echoReturnLossEnhancement` es cuánto eco está QUITANDO, en dB. Un
      // cancelador sano da decenas; el 15/10/2026, con el gestor llamando por
      // los altavoces del portátil, valía **0,1755** — o sea, no quitaba nada.
      // Estaba descoyuntado, y un cancelador descoyuntado no deja de suprimir:
      // suprime **la voz del gestor** junto con el eco.
      //
      // Ese número estuvo delante desde las 11:33 de aquel día, en una captura
      // de `chrome://webrtc-internals`, y pasó desapercibido porque no se estaba
      // buscando. Grabándolo cada segundo, la próxima vez el diagnóstico es
      // inmediato en vez de costar un día.
      if (typeof s.echoReturnLoss === 'number') out.ecoDevuelto = s.echoReturnLoss;
      if (typeof s.echoReturnLossEnhancement === 'number') {
        out.ecoCancelado = s.echoReturnLossEnhancement;
      }
    }
    if (s.type === 'outbound-rtp' && s.kind === 'audio') {
      if (typeof s.bytesSent === 'number') out.bytes = s.bytesSent;
      if (typeof s.packetsSent === 'number') out.paquetes = s.packetsSent;
    }
  });
  return out;
}

/**
 * Una muestra: el estado completo de la salida de audio en un instante.
 *
 * Exportada para poder probarla sin navegador.
 */
export async function tomarMuestra(pc, ahora = Date.now()) {
  const { tx, sender, track } = piezasDeAudio(pc);

  let stats = {};
  try {
    if (pc && typeof pc.getStats === 'function') stats = deLasEstadisticas(await pc.getStats());
  } catch {
    // Una conexión que ya murió tira aquí. Se anota como muestra sin medios en
    // vez de perderla: saber que `getStats` falló también es un dato.
  }

  return {
    t: ahora,
    ...stats,
    // Dirección negociada. `direction` es lo que PEDIMOS; `currentDirection` es
    // lo que de verdad quedó, y es el que importa.
    direccionPedida: tx?.direction ?? null,
    direccionReal: tx?.currentDirection ?? null,
    // La pista del micrófono. `muted` es de Windows/Chrome —el dispositivo dejó
    // de entregar— y NO lo controla la aplicación; `enabled` sí, y lo pone el
    // mute del gestor. Confundirlos daría el diagnóstico al revés.
    pistaId: track?.id ?? null,
    pistaEstado: track?.readyState ?? null,
    pistaMudaPorElSistema: track?.muted ?? null,
    pistaHabilitada: track?.enabled ?? null,
    pistaEtiqueta: track?.label ?? null,
    hayEmisor: Boolean(sender),
    // La conexión.
    señalizacion: pc?.signalingState ?? null,
    conexion: pc?.connectionState ?? null,
    hielo: pc?.iceConnectionState ?? null,
  };
}

/**
 * Arranca la grabación sobre una llamada del SDK.
 *
 * @returns {{apuntar: Function, volcar: Function, parar: Function}}
 */
export function grabar(call, opciones = {}) {
  const {
    muestreoMs = MUESTREO_MS,
    setIntervalFn = setInterval,
    clearIntervalFn = clearInterval,
    ahora = () => Date.now(),
  } = opciones;

  const pc = call?.peer?.instance || null;
  const muestras = [];
  const eventos = [];
  const sueltas = [];

  const apuntar = (que, datos = {}) => {
    eventos.push({ t: ahora(), que, ...datos });
    if (eventos.length > MAX_EVENTOS) eventos.shift();
  };

  // ⚠️ `addEventListener`, NUNCA `pc.onalgo = …`: esos huecos son del SDK de
  // Telnyx y pisarlos rompería la llamada que estamos observando.
  const escuchar = (obj, tipo, fn) => {
    if (!obj || typeof obj.addEventListener !== 'function') return;
    obj.addEventListener(tipo, fn);
    sueltas.push(() => obj.removeEventListener(tipo, fn));
  };

  if (pc) {
    escuchar(pc, 'signalingstatechange', () => {
      apuntar('señalizacion', { estado: pc.signalingState });
      // En `stable` acaba de cerrarse una negociación: es cuando las dos
      // descripciones son las definitivas y la dirección ya está decidida.
      if (pc.signalingState === 'stable') {
        apuntar('renegociada', {
          sdpLocal: direccionDelSdp(pc.currentLocalDescription?.sdp),
          sdpRemoto: direccionDelSdp(pc.currentRemoteDescription?.sdp),
          direccionReal: piezasDeAudio(pc).tx?.currentDirection ?? null,
        });
      }
    });
    escuchar(pc, 'connectionstatechange', () => apuntar('conexion', { estado: pc.connectionState }));
    escuchar(pc, 'iceconnectionstatechange', () => apuntar('hielo', { estado: pc.iceConnectionState }));
    escuchar(pc, 'negotiationneeded', () => apuntar('hace_falta_renegociar'));
  }

  // 🔴 El evento que puede cerrar esto. Si Windows le quita el dispositivo a
  // Chrome, la pista NO termina: dispara `mute`, sigue viva y enganchada, y
  // entrega silencio digital. Es exactamente el síntoma que no sabemos explicar.
  const { track } = piezasDeAudio(pc);
  if (track) {
    apuntar('pista_inicial', { id: track.id, etiqueta: track.label, muda: track.muted });
    escuchar(track, 'mute', () => apuntar('pista_MUDA', { id: track.id }));
    escuchar(track, 'unmute', () => apuntar('pista_recuperada', { id: track.id }));
    escuchar(track, 'ended', () => apuntar('pista_terminada', { id: track.id }));
  }

  // Si Windows cambia el dispositivo por defecto a media jornada, aquí consta.
  if (navigator?.mediaDevices) {
    escuchar(navigator.mediaDevices, 'devicechange', () => apuntar('cambio_de_dispositivos'));
  }

  const tick = async () => {
    const m = await tomarMuestra(pc, ahora());
    muestras.push(m);
    if (muestras.length > MAX_MUESTRAS) muestras.shift();
  };

  const id = setIntervalFn(tick, muestreoMs);

  return {
    apuntar,
    volcar: () => ({
      muestras: [...muestras],
      eventos: [...eventos],
      entorno: {
        agente: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        // La hora del portátil, para poder cruzar con los sellos del servidor y
        // medir de paso cuánto se desvía.
        reloj: new Date(ahora()).toISOString(),
      },
    }),
    parar: () => {
      clearIntervalFn(id);
      sueltas.forEach((f) => {
        try {
          f();
        } catch {
          // Un nodo que ya murió. No importa: se está parando.
        }
      });
      sueltas.length = 0;
    },
  };
}
